import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { memberLabel } from "@/components/OrgMemberSelect";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Spinner } from "@/components/ui/spinner";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";

type OrganisationUserResponse = components["schemas"]["OrganisationUserResponse"];

/** Whose spend a view shows. "Mine" and a person are views, not permissions: everyone may see all of it. */
export type SpenderScope =
  | { kind: "everyone" }
  | { kind: "mine" }
  | { kind: "person"; userId: string; label: string };

// Enough to pick from without scrolling; typing narrows it on the server, so a large organisation still works.
const PAGE = 10;

function scopeLabel(scope: SpenderScope) {
  return scope.kind === "everyone" ? "Everyone" : scope.kind === "mine" ? "My spend" : scope.label;
}

export function SpenderFilter({
  value,
  onChange,
  className,
}: {
  value: SpenderScope;
  onChange: (scope: SpenderScope) => void;
  className?: string;
}) {
  const api = useDolfinClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 400);
  const [members, setMembers] = useState<OrganisationUserResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const request = useRef(0);

  useEffect(() => {
    if (!open) return;
    const id = ++request.current;
    setLoading(true);
    api
      .GET("/v1/users", { params: { query: { Search: debounced.trim() || undefined, Limit: PAGE } } })
      .then(({ data }) => {
        if (id === request.current) setMembers((data?.data ?? []).filter((m) => m.id));
      })
      .catch(() => {
        if (id === request.current) setMembers([]);
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [api, debounced, open]);

  const choose = (scope: SpenderScope) => {
    onChange(scope);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Whose spend"
          className={cn("h-9 w-56 justify-between font-normal", className)}
        >
          <span className="truncate">{scopeLabel(value)}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        {/* The server does the matching, so cmdk's own filtering is off. */}
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search people..." value={query} onValueChange={setQuery} />
          <CommandList>
            {!query.trim() ? (
              <>
                <CommandGroup>
                  <CommandItem value="everyone" onSelect={() => choose({ kind: "everyone" })}>
                    <Check className={cn("size-4", value.kind === "everyone" ? "opacity-100" : "opacity-0")} />
                    Everyone
                  </CommandItem>
                  <CommandItem value="mine" onSelect={() => choose({ kind: "mine" })}>
                    <Check className={cn("size-4", value.kind === "mine" ? "opacity-100" : "opacity-0")} />
                    My spend
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            ) : null}
            <CommandGroup heading="People">
              {loading && members.length === 0 ? (
                <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                  <Spinner className="size-4" /> Loading...
                </div>
              ) : null}
              {members.map((m) => {
                const selected = value.kind === "person" && value.userId === m.id;
                return (
                  <CommandItem
                    key={m.id}
                    value={m.id!}
                    onSelect={() => choose({ kind: "person", userId: m.id!, label: memberLabel(m) })}
                  >
                    <Check className={cn("size-4", selected ? "opacity-100" : "opacity-0")} />
                    <span className="min-w-0">
                      <span className="block truncate">{memberLabel(m)}</span>
                      {m.name && m.email ? (
                        <span className="block truncate text-xs text-muted-foreground">{m.email}</span>
                      ) : null}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {!loading && members.length === 0 ? <CommandEmpty>No one matches.</CommandEmpty> : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
